import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  Output,
  Renderer2
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';

import { RenderingContext, RenderingContextQueryResult } from './../../../api';
import {
  createSingleSubject,
  DEFAULT_FETCH_LEVELS,
  DEFAULT_LAYOUT_MODE,
  KEY_LAYOUT_MODE,
  KEY_LEVELS,
  opBoxLayoutMode,
  opLevels,
  queryToString,
  opShareLast,
  perfMeasure
} from './../../../utils';
import { WchService } from './../../services/wch.service';
import { defineProperties } from './../../utils/js.utils';
import { QueryInput } from './../../utils/url.utils.ifaces';
import {
  AbstractLifeCycleComponent,
  createSetter
} from './../rendering/abstract.lifecycle.component';

/* Copyright IBM Corp. 2017 */

const LOGGER = 'ContentQueryComponent';

@Component({
  selector: 'wch-contentquery',
  templateUrl: './contentquery.component.html',
  styleUrls: ['./contentquery.component.css'],
  preserveWhitespaces: false
})
export class ContentQueryComponent extends AbstractLifeCycleComponent
  implements AfterViewInit, OnDestroy {
  /*
   * Set the layout mode to render this page
   *
   * @param aValue the new layout mode, if nothing is given this defaults to 'default'
   */
  @Input() layoutMode: string;

  /*
   * Accepts the query of the addressed component as an input
   */
  @Input() query: QueryInput;

  /*
   * Accepts the number of levels to be queried
   */
  @Input() levels: string | number | null | undefined;

  /*
  * Fires whenever the layout mode changed, includes the initial default mode.
  */
  @Output() readonly onLayoutMode: Observable<string>;

  /*
   * This monitors the current rendering context
   */
  @Output() readonly onRenderingContexts: Observable<RenderingContext[]>;

  /*
   * Total number of items changed
   */
  @Output() readonly onNumFound: Observable<number>;

  constructor(
    aRenderer: Renderer2,
    aElement: ElementRef,
    aWchService: WchService
  ) {
    super();
    // back reference
    const that = this;

    // measure startup
    const handle = perfMeasure(LOGGER);

    // the destroy handler
    const onDestroy = <T>() => takeUntil<T>(that.onOnDestroy);

    // handle the query levels
    const levelsSubject = new BehaviorSubject<
      string | number | null | undefined
    >(DEFAULT_FETCH_LEVELS);

    const onLevels = levelsSubject.pipe(
      opLevels,
      distinctUntilChanged()
    );

    // the subjects
    const modeSubject = new BehaviorSubject<string>(DEFAULT_LAYOUT_MODE);

    // define the setters
    const onLayoutMode = modeSubject.pipe(
      opBoxLayoutMode,
      distinctUntilChanged()
    );

    // the query
    const querySubject = createSingleSubject<QueryInput>();

    // constructs the native comment element
    const onNativeComment = that.onAfterViewInit.pipe(
      map<void, any>(() => aRenderer.createComment(LOGGER)),
      tap<any>(nativeComment =>
        aRenderer.appendChild(aElement.nativeElement, nativeComment)
      ),
      opShareLast
    );

    // query as a string
    const onQuery: Observable<string> = querySubject.pipe(
      // convert the query to a string
      map<QueryInput, string>(queryToString),
      // only react to changes
      distinctUntilChanged(),
      // share
      opShareLast
    );

    // the updated comment
    const onUpdateComment: Observable<any> = combineLatest(
      onQuery,
      onNativeComment
    ).pipe(
      tap<[string, any]>(([query, comment]) =>
        aRenderer.setValue(comment, query)
      ),
      onDestroy()
    );

    // the countable result
    const onSearchResult: Observable<
      RenderingContextQueryResult
    > = onQuery.pipe(
      // fetch the context from our service
      switchMap<string, RenderingContextQueryResult>(query =>
        aWchService.getRenderingContextsByQuery(query)
      ),
      // cache the last value
      opShareLast
    );

    // build the chain
    const onRenderingContexts: Observable<
      RenderingContext[]
    > = onSearchResult.pipe(
      map<RenderingContextQueryResult, RenderingContext[]>(
        res => res.renderingContexts
      )
    );

    const onNumFound: Observable<number> = onSearchResult.pipe(
      map<RenderingContextQueryResult, number>(res => res.numFound)
    );

    // define the setters
    defineProperties(that, {
      query: createSetter(querySubject, that),
      [KEY_LEVELS]: createSetter(levelsSubject, that),
      [KEY_LAYOUT_MODE]: createSetter(modeSubject, that)
    });

    // define the output handlers
    this.onLayoutMode = onLayoutMode.pipe(onDestroy());
    this.onRenderingContexts = onRenderingContexts.pipe(onDestroy());
    this.onNumFound = onNumFound.pipe(onDestroy());

    // update the comment
    onUpdateComment.subscribe();

    handle();
  }

  // just dispatch, otherwise the method will not be recognized
  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  // just dispatch, otherwise the method will not be recognized
  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
